/**
 * Chat-action lifecycle reducer.
 *
 * The previous implementation tracked the lifecycle across several
 * disconnected booleans/setters: `isStreaming` (in useSendMessage),
 * `storeStatus` (in useStoreConversation), `error` (in the composition
 * root), `networkErrorActive` (cached on the conversation object).
 * They occasionally disagreed — e.g. an error firing before the first
 * SSE token would leave `isStreaming === false` while
 * `assistantMessage.thinking === true`.
 *
 * Phase 5 of plans/useChatHook-enterprise-grade.md collapses the
 * action lifecycle into a single explicit state machine. Network-level
 * loading state (React Query's `isLoading`/`isFetching` for the
 * conversation fetch) deliberately stays out — those concerns are
 * orthogonal and React Query is the source of truth for them.
 *
 *   idle ─┬─► streaming ─┬─► (sendMessage returns; caller invokes
 *         │              │    store.save) ─► saving ─► saved ─► idle
 *         │              ├─► error      (stream failed)
 *         │              └─► idle       (cancelled)
 *         │
 *         └─► error      (fetch failure surfaced via FETCH_ERROR)
 *
 * `saving → saved` auto-transitions to `idle` after 3 seconds (the
 * "Synced with server" toast window). The reducer doesn't own the
 * timer — see useChatHook for the effect that fires SAVED_TIMEOUT.
 */

export const initialChatState = {
  phase: "idle", // "idle" | "streaming" | "saving" | "saved" | "error"
  error: null, // { kind, message } | null
  saveData: null, // last successful save response payload
};

export function chatReducer(state, action) {
  switch (action.type) {
    case "STREAM_START":
      // Clear any lingering error from a previous run; the user is
      // taking a new action and a stale toast would just be noise.
      return { ...state, phase: "streaming", error: null };

    case "STREAM_COMPLETE":
      // Stream wrapped successfully. The composition root will fire
      // SAVE_START next; in the brief gap we land in idle.
      return state.phase === "streaming"
        ? { ...state, phase: "idle" }
        : state;

    case "STREAM_ERROR":
      return {
        ...state,
        phase: "error",
        error: { kind: "stream", message: action.message },
      };

    case "STREAM_CANCELLED":
      return state.phase === "streaming"
        ? { ...state, phase: "idle" }
        : state;

    case "SAVE_START":
      return { ...state, phase: "saving" };

    case "SAVE_SUCCESS":
      return { ...state, phase: "saved", saveData: action.data ?? null };

    case "SAVE_ERROR":
      return {
        ...state,
        phase: "error",
        error: { kind: "store", message: action.message },
      };

    case "SAVED_TIMEOUT":
      return state.phase === "saved"
        ? { ...state, phase: "idle", saveData: null }
        : state;

    case "FETCH_ERROR":
      // Don't clobber an in-flight stream/save with a fetch error.
      // Only surface it when nothing more useful is happening.
      if (state.phase === "streaming" || state.phase === "saving") {
        return state;
      }
      return {
        ...state,
        phase: "error",
        error: { kind: "fetch", message: action.message },
      };

    case "FETCH_RECOVERED":
      // Conversation fetch succeeded after an earlier failure — drop
      // the stale fetch error if it's still showing. Don't touch
      // store/stream errors.
      if (state.error?.kind === "fetch") {
        return { ...state, phase: "idle", error: null };
      }
      return state;

    case "DISMISS_ERROR":
      return state.phase === "error"
        ? { ...state, phase: "idle", error: null }
        : { ...state, error: null };

    default:
      return state;
  }
}
